import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../global/Layout';
import { createUser, getUsers, sendInvite, updateUser, updateUserStatus, getAllStores } from '../../lib/admin';
import CONSTANTS from '../../lib/constants';
import { UsersTable } from './UserTable';
import { UserToolbar } from './UserToolbar';
import { UserCreateModal } from './UserCreateModal';
import { UserEditModal } from './UserEditModal';
import { ConfirmModal } from './ConfirmModal';
import { ScopeModal } from './ScopesModal';
import Pagination from '../global/Pagination';

export type PlatformUser = {
    _id: string;
    email: string;
    scopes: string[];
    stores: string[];
    isActive: boolean;
};

type StoreApi = {
    id?: number;
    friendlyName?: string;
    logo?: string;
};

export type StoreOption = { id: number; name: string; logo?: string };

const toggleScope = (current: string[], scope: string): string[] => {
    const group = scope === CONSTANTS.DELETE_STORE_SCOPE ? CONSTANTS.DELETE_STORE_SCOPES : [scope];

    return current.includes(scope)
        ? current.filter(v => !group.includes(v))
        : [...current.filter(v => !group.includes(v)), ...group];
};

type UsersPage = {
    items: PlatformUser[];
    total: number;
    page: number;
    pageSize: number;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

export default function AdminUsersContainer() {
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);

    const [users, setUsers] = useState<PlatformUser[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedScopeFilter, setSelectedScopeFilter] = useState<'all' | string>('all');
    const [isSortByEmailAsc, setIsSortByEmailAsc] = useState(true);

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createEmail, setCreateEmail] = useState('');
    const [createScopes, setCreateScopes] = useState<string[]>([]);
    const [createStoreId, setCreateStoreId] = useState<number>(0);
    const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
    const [createFeedback, setCreateFeedback] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<PlatformUser | null>(null);
    const [editEmail, setEditEmail] = useState('');
    const [editScopes, setEditScopes] = useState<string[]>([]);
    const [editIsActive, setEditIsActive] = useState<boolean>(true);
    const [editStoreId, setEditStoreId] = useState<number>(0);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [editFeedback, setEditFeedback] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

    const [inviteUser, setInviteUser] = useState<PlatformUser | null>(null);
    const [isSendingInvite, setIsSendingInvite] = useState(false);
    const [inviteFeedback, setInviteFeedback] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

    const [scopeUser, setScopeUser] = useState<PlatformUser | null>(null);

    const [toggleUser, setToggleUser] = useState<PlatformUser | null>(null);
    const [isToggling, setIsToggling] = useState(false);
    const [toggleFeedback, setToggleFeedback] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

    const [stores, setStores] = useState<StoreOption[]>([]);
    const storeMap = useMemo(() => {
        const m: Record<string, StoreOption> = {};
        stores.forEach(s => {
            m[s.id] = s;
        });
        return m;
    }, [stores]);

    function openScopes(u: PlatformUser) {
        setScopeUser(u);
    }

    function closeScopes() {
        setScopeUser(null);
    }

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const storesRes = (await getAllStores().catch(() => [])) as StoreApi[] | [];
                if (!cancelled) {
                    const parsedStores = (storesRes ?? [])
                        .map<StoreOption>((s: StoreApi) => ({
                            id: Number(s.id ?? ''),
                            name: String(s.friendlyName ?? s.id ?? ''),
                            logo: s.logo ? String(s.logo) : undefined,
                        }))
                        .filter((s): s is StoreOption => Boolean(s.id));
                    setStores(parsedStores);
                }
            } catch { }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
        return () => clearTimeout(t);
    }, [searchQuery]);

    async function fetchUsersPage() {
        setIsLoadingUsers(true);
        setLoadErrorMessage(null);
        try {
            const data = (await getUsers({
                sort: isSortByEmailAsc ? 'asc' : 'desc',
                limit: CONSTANTS.PAGE_SIZE,
                page,
                search: debouncedSearch || undefined,
                scope: selectedScopeFilter === 'all' ? undefined : selectedScopeFilter,
            })) as UsersPage;
            setUsers(Array.isArray(data.items) ? data.items : []);
            setTotal(Number(data.total ?? 0));
        } catch (e: any) {
            setLoadErrorMessage(e?.message ?? 'Failed to load users');
        } finally {
            setIsLoadingUsers(false);
        }
    }

    useEffect(() => {
        fetchUsersPage();
    }, [page, debouncedSearch, selectedScopeFilter, isSortByEmailAsc]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, selectedScopeFilter, isSortByEmailAsc]);

    function openCreate() {
        setCreateEmail('');
        setCreateScopes([]);
        setCreateStoreId(0);
        setCreateFeedback({ type: '', text: '' });
        setIsCreateModalOpen(true);
    }

    function closeCreate() {
        if (isSubmittingCreate) return;
        setIsCreateModalOpen(false);
        setCreateFeedback({ type: '', text: '' });
    }

    async function submitCreate(e: React.FormEvent) {
        e.preventDefault();
        if (!EMAIL_RE.test(createEmail)) {
            setCreateFeedback({ type: 'error', text: 'Invalid email address.' });
            return;
        }
        setIsSubmittingCreate(true);
        setCreateFeedback({ type: '', text: '' });
        try {
            const storesPayload = [Number(createStoreId) || 0];
            await createUser({
                email: createEmail.trim().toLowerCase(),
                scopes: createScopes,
                isActive: true,
                stores: storesPayload,
            } as any);
            setPage(1);
            await fetchUsersPage();
            setCreateFeedback({ type: 'success', text: 'User has been created successfully.' });
            setTimeout(() => {
                setIsCreateModalOpen(false);
                setCreateFeedback({ type: '', text: '' });
            }, 1200);
        } catch (err: any) {
            setCreateFeedback({ type: 'error', text: err?.message || 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingCreate(false);
        }
    }


    function openEdit(u: PlatformUser) {
        setEditingUser(u);
        setEditEmail(u.email);
        setEditScopes(u.scopes || []);
        setEditIsActive(!!u.isActive);
        setEditStoreId(Number(u.stores?.[0] ?? 0));
        setEditFeedback({ type: '', text: '' });
        setIsEditModalOpen(true);
    }

    function closeEdit() {
        if (isSubmittingEdit) return;
        setIsEditModalOpen(false);
        setEditFeedback({ type: '', text: '' });
    }

    async function submitEdit(e: React.FormEvent) {
        e.preventDefault();
        if (!editingUser) return;
        if (!EMAIL_RE.test(editEmail)) {
            setEditFeedback({ type: 'error', text: 'Invalid email address.' });
            return;
        }
        setIsSubmittingEdit(true);
        setEditFeedback({ type: '', text: '' });
        try {
            const storesPayload = [Number(editStoreId) || 0];
            await updateUser(editingUser._id, {
                email: editEmail.trim().toLowerCase(),
                scopes: editScopes,
                isActive: editIsActive,
                stores: storesPayload,
            } as any);
            await fetchUsersPage();
            setEditFeedback({ type: 'success', text: 'User updated successfully.' });
            setTimeout(() => {
                setIsEditModalOpen(false);
                setEditFeedback({ type: '', text: '' });
            }, 1000);
        } catch (err: any) {
            setEditFeedback({ type: 'error', text: err?.message || 'An unexpected error occurred.' });
        } finally {
            setIsSubmittingEdit(false);
        }
    }


    const handleGoBack = () => (window.location.href = '/stores');

    return (
        <Layout>
            <button className="back-button" onClick={handleGoBack}>Back to dashboard</button>
            <div className="page admin-users">
                <div className="container">
                    <h1 className="page-title">User Management ({total})</h1>
                    <UserToolbar
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        selectedScope={selectedScopeFilter}
                        onScopeChange={v => setSelectedScopeFilter(v)}
                        scopes={CONSTANTS.ALL_SCOPES}
                        onNewUser={openCreate}
                    />
                    <div className="table-box">
                        {isLoadingUsers ? (
                            <div className="empty">Loading…</div>
                        ) : loadErrorMessage ? (
                            <div className="empty">Error: {loadErrorMessage}</div>
                        ) : (
                            <UsersTable
                                users={users}
                                sortAsc={isSortByEmailAsc}
                                onToggleSort={() => setIsSortByEmailAsc(s => !s)}
                                onEdit={openEdit}
                                onToggleActive={(u) => {
                                    setToggleFeedback({ type: '', text: '' });
                                    setToggleUser(u);
                                }}
                                onSendInvite={setInviteUser}
                                onViewScopes={openScopes}
                                storeMap={storeMap}
                            />
                        )}
                    </div>

                    {!isLoadingUsers && !loadErrorMessage && (
                        <div className="admin-users__pagination">
                            <Pagination
                                pageSize={CONSTANTS.PAGE_SIZE}
                                totalItems={total}
                                currentPage={page}
                                paginate={setPage}
                                disableEdges
                            />
                        </div>
                    )}
                </div>
                <UserCreateModal
                    open={isCreateModalOpen}
                    email={createEmail}
                    scopes={createScopes}
                    allScopes={CONSTANTS.ALL_SCOPES}
                    onEmailChange={setCreateEmail}
                    onToggleScope={s => setCreateScopes(prev => toggleScope(prev, s))}
                    onClose={closeCreate}
                    onSubmit={submitCreate}
                    submitting={isSubmittingCreate}
                    feedback={createFeedback}
                    stores={stores}
                    storeId={createStoreId}
                    onStoreChange={setCreateStoreId}
                />
                {editingUser && (
                    <UserEditModal
                        open={isEditModalOpen}
                        email={editEmail}
                        scopes={editScopes}
                        isActive={editIsActive}
                        allScopes={CONSTANTS.ALL_SCOPES}
                        onEmailChange={setEditEmail}
                        onToggleScope={s => setEditScopes(prev => toggleScope(prev, s))}
                        onToggleActive={() => setEditIsActive(v => !v)}
                        onClose={closeEdit}
                        onSubmit={submitEdit}
                        submitting={isSubmittingEdit}
                        feedback={editFeedback}
                        stores={stores}
                        storeId={editStoreId}
                        onStoreChange={setEditStoreId}
                    />
                )}
                <ConfirmModal
                    open={!!inviteUser}
                    title="Send invite"
                    message={inviteUser ? `Send a one-time sign-in link to ${inviteUser.email}?` : ''}
                    primaryLabel={isSendingInvite ? 'Sending...' : 'Send invite'}
                    onConfirm={async () => {
                        if (!inviteUser) return;
                        setIsSendingInvite(true);
                        setInviteFeedback({ type: '', text: '' });
                        try {
                            await sendInvite(inviteUser._id);
                            setInviteFeedback({ type: 'success', text: 'Invitation link sent.' });
                            setTimeout(() => setInviteUser(null), 1000);
                        } catch (e: any) {
                            setInviteFeedback({ type: 'error', text: e?.message || 'Could not send the link.' });
                        } finally {
                            setIsSendingInvite(false);
                        }
                    }}
                    onClose={() => {
                        if (isSendingInvite) return;
                        setInviteUser(null);
                        setInviteFeedback({ type: '', text: '' });
                    }}
                    submitting={isSendingInvite}
                    feedback={inviteFeedback}
                />
                <ConfirmModal
                    open={!!toggleUser}
                    title={toggleUser ? (toggleUser.isActive ? 'Disable user' : 'Enable user') : 'Confirm'}
                    message={toggleUser ? `Are you sure you want to ${toggleUser.isActive ? 'disable' : 'enable'} ${toggleUser.email}?` : ''}
                    primaryLabel={isToggling ? 'Processing...' : toggleUser?.isActive ? 'Disable' : 'Enable'}
                    onConfirm={async () => {
                        if (!toggleUser) return;
                        setIsToggling(true);
                        setToggleFeedback({ type: '', text: '' });
                        const next = !toggleUser.isActive;
                        try {
                            await updateUserStatus(toggleUser._id, next, toggleUser.email);
                            await fetchUsersPage();
                            setToggleFeedback({ type: 'success', text: 'Status updated.' });
                            setTimeout(() => setToggleUser(null), 800);
                        } catch (e: any) {
                            setToggleFeedback({ type: 'error', text: e?.message || 'Could not update status.' });
                        } finally {
                            setIsToggling(false);
                        }
                    }}
                    onClose={() => {
                        if (isToggling) return;
                        setToggleUser(null);
                        setToggleFeedback({ type: '', text: '' });
                    }}
                    submitting={isToggling}
                    feedback={toggleFeedback}
                />
                <ScopeModal open={!!scopeUser} title={scopeUser ? `Scopes for ${scopeUser.email}` : 'Scopes'} scopes={scopeUser?.scopes || []} onClose={closeScopes} />
            </div>
        </Layout>
    );
}
